import { useState, type ReactNode } from 'react';
import {
  Building2,
  CheckCircle2,
  ChevronDown,
  KeyRound,
  Landmark,
  Receipt,
  Search,
  SlidersHorizontal,
  UserRound,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import { formatDate, formatEuro, formatInteger, formatSquareMeters } from '@brimo/shared';
import { clsx } from '../../../ds/clsx';
import { Icon, TextLink, Toggle } from '../../../ds/components';
import { useStableId } from '../../../ds/hooks';
import { useContactSearch } from '../api';
import { rentFactRows } from '../rent';
import type { CalculatorContext, InvestmentInputValues } from '../types';
import {
  ADDITIONAL_DEPRECIATION_PARAM,
  ASSUMPTION_PARAMS,
  COST_PARAMS,
  DEPRECIATION_MANUAL_PARAM,
  EQUITY_PARAM,
  FINANCING_PARAMS,
  FINANCING_SHARE_PARAM,
  INPUT_INFO,
  OBJECT_PARAMS,
  RENT_INCREASE_PARAM,
  RENT_INTERVAL_PARAM,
  RENT_PARAMS,
  deductibleSum,
  equityOf,
  eur,
  financingShareOf,
  isCostSplitKey,
  linearDepreciationCents,
  pct,
  sliderRange,
  sourceHint,
  sourceLink,
  subsidyMonths,
  taxRateSource,
  toEditValue,
  fromEditValue,
  type CostSplit,
  type ParamKey,
  type ParamSpec,
} from './model';
import { InfoTip } from './InfoTip';
import { NumberField } from './NumberField';
import styles from './calculator.module.css';

interface AccordionProps {
  icon: ReactNode;
  title: string;
  subtitle: string;
  open: boolean;
  onToggle: () => void;
  fields?: boolean | undefined;
  children: ReactNode;
}

export function Accordion({
  icon,
  title,
  subtitle,
  open,
  onToggle,
  fields = false,
  children,
}: AccordionProps) {
  const id = useStableId();
  return (
    <div className={styles.accordion}>
      <button
        type="button"
        className={styles.accordionHead}
        aria-expanded={open}
        aria-controls={id}
        onClick={onToggle}
      >
        <span className={styles.accordionIcon}>{icon}</span>
        <span className={styles.accordionText}>
          <span className={styles.accordionTitle}>{title}</span>
          <span className={styles.accordionSub}>{subtitle}</span>
        </span>
        <span className={clsx(styles.accordionChevron, open && styles.accordionChevronOpen)}>
          <Icon icon={ChevronDown} size={16} />
        </span>
      </button>
      {open ? (
        <div id={id} className={clsx(styles.accordionBody, fields && styles.accordionBodyFields)}>
          {children}
        </div>
      ) : null}
    </div>
  );
}

function KvRow({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.kvRow}>
      <span className={styles.kvLabel}>{label}</span>
      <span className={styles.kvValue}>{value}</span>
    </div>
  );
}

function ContactSearch({ onSelect }: { onSelect: (contactId: string) => void }) {
  const [query, setQuery] = useState('');
  const search = useContactSearch(query, query.trim().length >= 2);
  const hits = search.data?.items ?? [];
  const show = query.trim().length >= 2;
  const id = useStableId();
  return (
    <div className={styles.searchWrap}>
      <div className={styles.calcSearchField}>
        <Icon icon={Search} size={16} />
        <input
          id={id}
          type="search"
          aria-label="Mandant suchen"
          placeholder="Mandant suchen — Name oder Mandatsnummer"
          value={query}
          autoComplete="off"
          onChange={(event) => setQuery(event.target.value)}
        />
      </div>
      {show ? (
        <div className={styles.hits} role="listbox" aria-label="Treffer">
          {hits.length === 0 ? (
            <div className={styles.hitEmpty}>{search.isPending ? 'Suche …' : 'Kein Kontakt gefunden.'}</div>
          ) : (
            hits.map((hit) => (
              <button
                key={hit.id}
                type="button"
                role="option"
                aria-selected={false}
                className={styles.hit}
                onClick={() => {
                  setQuery('');
                  onSelect(hit.id);
                }}
              >
                {hit.lastName}, {hit.firstName}
                {hit.deal ? ` · ${hit.deal}` : ''}
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}

function stored(spec: ParamSpec, values: InvestmentInputValues, split: CostSplit): number {
  if (isCostSplitKey(spec.key)) return split[spec.key];
  const value = values[spec.key];
  return typeof value === 'number' ? value : 0;
}

/** Typed number field plus, where the spec has one, the slider bound to the same value. */
function ParamControl({
  spec,
  values,
  split,
  onChange,
  hint,
  info,
  disabled,
  value: shown,
}: {
  spec: ParamSpec;
  values: InvestmentInputValues;
  split: CostSplit;
  onChange: (key: ParamKey, value: number) => void;
  hint?: ReactNode | undefined;
  info?: string | undefined;
  disabled?: boolean | undefined;
  /** The value to show when it is derived (equity from a financing share and vice versa). */
  value?: number | undefined;
}) {
  const value = shown ?? stored(spec, values, split);
  const range = sliderRange(spec, values);
  const editValue = toEditValue(spec, value);
  return (
    <div className={styles.param}>
      <NumberField
        label={spec.label}
        value={value}
        unit={spec.unit}
        step={spec.step}
        min={spec.min}
        max={spec.max}
        hint={hint}
        info={info}
        disabled={disabled}
        onChange={(next) => onChange(spec.key, next)}
      />
      {range ? (
        <input
          type="range"
          className={styles.range}
          aria-label={spec.label}
          min={range.min}
          max={range.max}
          step={range.step}
          value={Math.min(range.max, Math.max(range.min, editValue))}
          disabled={disabled}
          onChange={(event) => onChange(spec.key, fromEditValue(spec, Number(event.target.value)))}
        />
      ) : null}
    </div>
  );
}

/** A switch of the inputs with its (i) explanation. */
function InfoToggle({
  label,
  info,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  info: string;
  checked: boolean;
  disabled?: boolean | undefined;
  onChange: (checked: boolean) => void;
}) {
  return (
    <span className={styles.toggleRow}>
      <Toggle
        label={label}
        checked={checked}
        disabled={disabled}
        onChange={onChange}
        className={styles.paramToggle}
      />
      <InfoTip label={label} text={info} />
    </span>
  );
}

export type InputGroupKey = 'object' | 'depreciation' | 'financing' | 'rent' | 'costs';

/** The parameter groups of the inputs column in their order; the collapsed rail offers one shortcut per group. */
export const INPUT_GROUPS: { key: InputGroupKey; title: string; icon: LucideIcon }[] = [
  { key: 'object', title: 'Objekt', icon: Building2 },
  { key: 'depreciation', title: 'Abschreibung (AfA)', icon: Receipt },
  { key: 'financing', title: 'Finanzierung', icon: Landmark },
  { key: 'rent', title: 'Miete', icon: KeyRound },
  { key: 'costs', title: 'Laufende Kosten (mtl.)', icon: Wallet },
];

/** Selector of a group's fieldset (`data-input-group`), so the collapsed rail can scroll it into view. */
export function inputGroupSelector(group: InputGroupKey): string {
  return `[data-input-group="${group}"]`;
}

function Group({ group, children }: { group: InputGroupKey; children: ReactNode }) {
  const title = INPUT_GROUPS.find((entry) => entry.key === group)?.title ?? '';
  return (
    <fieldset className={styles.paramGroup} data-input-group={group}>
      <legend className={styles.eyebrow11}>{title}</legend>
      <div className={styles.paramList}>{children}</div>
    </fieldset>
  );
}

export interface InputsColumnProps {
  context: CalculatorContext;
  values: InvestmentInputValues;
  split: CostSplit;
  onChange: (key: ParamKey, value: number) => void;
  onSellerPaysChange: (sellerPays: boolean) => void;
  /** Sets inputs that are not plain numbers (financing share, manual AfA, „Tilgung als Kosten", D-347). */
  onPatch: (patch: Partial<InvestmentInputValues>) => void;
  onContactChange: (contactId: string) => void;
}

/** The inputs of the Immobilienrechner: system facts (accordions) and every parameter as a typed number. */
export function InputsColumn({
  context,
  values,
  split,
  onChange,
  onSellerPaysChange,
  onPatch,
  onContactChange,
}: InputsColumnProps) {
  const [open, setOpen] = useState<'unit' | 'contact' | 'assumptions' | null>(null);
  const toggle = (key: 'unit' | 'contact' | 'assumptions') =>
    setOpen((current) => (current === key ? null : key));
  const { unit, contact } = context;
  const rentRows = rentFactRows({
    actualCents: unit.rentActualCents,
    targetCents: unit.rentTargetCents,
    subsidyCents: unit.rentSubsidyCents,
  });
  const locationLine = unit.address ? unit.address.split(', ').slice(0, 2).join(', ') : unit.propertyName;
  const money = (cents: number | null) => (cents === null ? '—' : `${eur(cents)} / Monat`);
  const sellerPays = values.sellerPaysAcquisitionCosts;
  const taxSource = taxRateSource(context);

  const baseHint = (key: ParamKey): string | null => {
    if (key === 'buildingSharePct') return `Grundstücksanteil ${pct(100 - values.buildingSharePct, 0)}`;
    if (key === 'taxRatePct') return taxSource;
    if (key === 'depreciationPct' && values.depreciationManualCents !== null) {
      return 'AfA manuell aktiv – der Satz wird nicht verwendet';
    }
    if (key === 'depreciationPct' && values.depreciationPct > 0) {
      return `Nutzungsdauer ${formatInteger(Math.round((100 / values.depreciationPct) * 10) / 10)} Jahre`;
    }
    if (key === 'rentSubsidyCycles' && values.rentSubsidyCycles > 0) {
      return `Soll-Miete für ${formatInteger(subsidyMonths(values))} Monate`;
    }
    if (key === 'acquisitionCostsPct') {
      return `${eur((values.purchasePriceCents * values.acquisitionCostsPct) / 100)} auf den Kaufpreis`;
    }
    return null;
  };
  /** The input's own hint plus, while it holds the prefilled value, where that came from (D-343). */
  const atDefault = (key: ParamKey): boolean =>
    isCostSplitKey(key) ? split[key] === context.costs[key] : values[key] === context.defaults[key];
  const share = financingShareOf(values);
  const manualDepreciation = values.depreciationManualCents;
  /** … plus the link „ändern" to where that origin is changed (D-360). */
  const hintFor = (key: ParamKey): ReactNode => {
    const source = atDefault(key) ? sourceHint(context, key) : null;
    const parts = [baseHint(key), source].filter((part): part is string => part !== null);
    if (parts.length === 0) return undefined;
    const href = source === null ? null : sourceLink(context, key);
    if (href === null) return parts.join(' · ');
    return (
      <>
        {parts.join(' · ')} ·{' '}
        <TextLink to={href} size="small">
          ändern
        </TextLink>
      </>
    );
  };

  return (
    <div className={styles.inputsColumn}>
      <Accordion
        icon={<Icon icon={CheckCircle2} size={20} className={styles.accordionIconDone} />}
        title="Objektdaten aus System"
        subtitle={`${locationLine} · ${unit.number}`}
        open={open === 'unit'}
        onToggle={() => toggle('unit')}
      >
        <KvRow label="Kaufpreis" value={unit.priceCents === null ? '—' : formatEuro(unit.priceCents)} />
        <KvRow label="Wohnfläche" value={unit.areaSqm === null ? '—' : formatSquareMeters(unit.areaSqm)} />
        <KvRow
          label="Preis je m²"
          value={unit.pricePerSqmCents === null ? '—' : eur(unit.pricePerSqmCents)}
        />
        {rentRows.map((row) => (
          <KvRow key={row.label} label={row.label} value={row.value} />
        ))}
        <KvRow label="Baujahr" value={unit.yearBuilt ?? '—'} />
        <KvRow
          label="Mieter seit"
          value={
            unit.rented
              ? unit.rentedSinceOn
                ? formatDate(unit.rentedSinceOn)
                : 'vermietet'
              : 'nicht vermietet'
          }
        />
        <KvRow label="SE-Verwaltung mtl." value={money(unit.sevCents)} />
        <KvRow label="WEG-Verwaltung mtl." value={money(unit.wegCents)} />
        <KvRow label="Sonstige nicht umlagefähige Kosten mtl." value={money(unit.otherNonRecoverableCents)} />
        <KvRow label="Instandhaltungsrücklage mtl." value={money(unit.reserveShareCents)} />
        <KvRow label="Hausgeld mtl." value={money(unit.serviceChargeCents)} />
        <KvRow label="Stellplatzmiete mtl." value={money(unit.parkingSpaceRentCents)} />
        <KvRow
          label="Verkäufer zahlt Kaufnebenkosten"
          value={unit.sellerPaysAcquisitionCosts ? 'Ja' : 'Nein'}
        />
      </Accordion>
      <Accordion
        icon={<Icon icon={UserRound} size={20} />}
        title="Kundendaten aus System"
        subtitle={contact ? contact.name : 'Mandant wählen'}
        open={open === 'contact'}
        onToggle={() => toggle('contact')}
      >
        <ContactSearch onSelect={onContactChange} />
        <KvRow label="Mandant" value={contact?.name ?? '—'} />
        <KvRow label="Persönlicher Steuersatz" value={`${pct(values.taxRatePct, 0)} · ${taxSource}`} />
        <KvRow
          label="Zu versteuerndes Einkommen"
          value={contact?.netIncomeCents ? `${formatEuro(contact.netIncomeCents * 12)} p. a.` : '—'}
        />
        <KvRow label="Familienstand" value={contact?.maritalStatus ?? '—'} />
        <KvRow
          label="Selbstauskunft"
          value={contact?.selfDisclosureVersion ? `V${formatInteger(contact.selfDisclosureVersion)}` : '—'}
        />
      </Accordion>
      <Accordion
        icon={<Icon icon={SlidersHorizontal} size={20} />}
        title="Annahmen (Organisationsvorgaben)"
        subtitle="Wertentwicklung, AfA, Gebäudeanteil, Steuersatz"
        open={open === 'assumptions'}
        onToggle={() => toggle('assumptions')}
        fields
      >
        {ASSUMPTION_PARAMS.map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
          />
        ))}
      </Accordion>

      <Group group="object">
        {OBJECT_PARAMS.slice(0, 2).map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
          />
        ))}
        <Toggle
          label="Verkäufer zahlt Kaufnebenkosten"
          checked={sellerPays}
          onChange={onSellerPaysChange}
          className={styles.paramToggle}
        />
        {OBJECT_PARAMS.slice(2).map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
          />
        ))}
      </Group>

      <Group group="depreciation">
        <InfoToggle
          label="AfA manuell"
          info={INPUT_INFO.depreciationManual}
          checked={manualDepreciation !== null}
          onChange={(on) => onPatch({ depreciationManualCents: on ? linearDepreciationCents(values) : null })}
        />
        {manualDepreciation !== null ? (
          <ParamControl
            spec={DEPRECIATION_MANUAL_PARAM}
            values={values}
            split={split}
            onChange={(_, next) => onPatch({ depreciationManualCents: next })}
            value={manualDepreciation}
            hint={`statt ${pct(values.depreciationPct, 2)} linear (${eur(linearDepreciationCents(values))} p.a.)`}
          />
        ) : null}
        <ParamControl
          spec={ADDITIONAL_DEPRECIATION_PARAM}
          values={values}
          split={split}
          onChange={onChange}
          info={INPUT_INFO.additionalDepreciationCents}
        />
      </Group>

      <Group group="financing">
        <ParamControl
          spec={EQUITY_PARAM}
          values={values}
          split={split}
          onChange={(_, next) => onPatch({ equityCents: next, financingSharePct: null })}
          value={equityOf(values)}
          disabled={sellerPays}
          hint={
            sellerPays
              ? 'Der Verkäufer zahlt die Kaufnebenkosten. Finanziert wird der volle Betrag ohne Eigenkapital.'
              : values.financingSharePct !== null
                ? `ergibt sich aus dem Finanzierungsanteil von ${pct(values.financingSharePct, 0)}`
                : undefined
          }
        />
        <ParamControl
          spec={FINANCING_SHARE_PARAM}
          values={values}
          split={split}
          onChange={(_, next) => onPatch({ financingSharePct: next })}
          value={Math.round(share * 10) / 10}
          disabled={sellerPays}
          info={INPUT_INFO.financingSharePct}
          hint={
            sellerPays
              ? undefined
              : `Darlehen ${eur((values.purchasePriceCents * share) / 100)} · ${share > 100 ? 'inkl. Nebenkosten finanziert' : 'des Kaufpreises'}`
          }
        />
        {FINANCING_PARAMS.map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
          />
        ))}
        <InfoToggle
          label="Tilgung als Kosten"
          info={INPUT_INFO.repaymentAsCost}
          checked={values.repaymentAsCost}
          onChange={(on) => onPatch({ repaymentAsCost: on })}
        />
      </Group>

      <Group group="rent">
        {RENT_PARAMS.map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
            info={spec.key === 'parkingSpaceRentCents' ? INPUT_INFO.parkingSpaceRentCents : undefined}
          />
        ))}
        <div className={styles.paramPair}>
          <ParamControl spec={RENT_INCREASE_PARAM} values={values} split={split} onChange={onChange} />
          <ParamControl spec={RENT_INTERVAL_PARAM} values={values} split={split} onChange={onChange} />
        </div>
      </Group>

      <Group group="costs">
        {COST_PARAMS.map((spec) => (
          <ParamControl
            key={spec.key}
            spec={spec}
            values={values}
            split={split}
            onChange={onChange}
            hint={hintFor(spec.key)}
          />
        ))}
        <div className={styles.paramSum}>
          <span>Absetzbar gesamt</span>
          <span className={styles.kvValue}>{eur(deductibleSum(split))} / Monat</span>
        </div>
      </Group>
    </div>
  );
}
